import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { retryOperation } from '@/shared/lib/api';
import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { extractModelNameFromFQN } from '@/shared/lib/mlflow';
import { assetKeys, discoveryKeys } from '@/shared/lib/query/queryKeys';

import { deleteModelTag, updateModelTag } from '@/entities/registered-model';
import type { TagOption } from '@/entities/tags';

import { type AssetBaseInfo, generateTagPatchOperations, updateAssetTags } from '@/features/asset-detail/api';

type UpdateTagsParams = {
  entityType: string;
  entityId: string;
  fqn: string;
  newTagFQNs: string[];
  availableTags: TagOption[];
};

export const useUpdateAssetTags = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ entityType, entityId, fqn, newTagFQNs, availableTags }: UpdateTagsParams) => {
      const currentData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));
      const oldTags = currentData?.tags || [];

      const patchOperations = generateTagPatchOperations(oldTags, newTagFQNs, availableTags);

      if (patchOperations.length === 0) {
        return;
      }

      await updateAssetTags(entityType, entityId, patchOperations);

      if (entityType === 'mlmodel') {
        const modelName = extractModelNameFromFQN(fqn);

        const oldTagFQNs = oldTags
          .filter((tag) => tag.source === 'Classification')
          .map((tag) => tag.tagFQN)
          .filter((fqn): fqn is string => !!fqn);

        const addedTagFQNs = newTagFQNs.filter((fqn) => !oldTagFQNs.includes(fqn));
        const removedTagFQNs = oldTagFQNs.filter((fqn) => !newTagFQNs.includes(fqn));

        const mlflowOperations: Promise<void>[] = [];
        const tagOptionsMap = new Map(availableTags.map((tag) => [tag.value, tag]));

        addedTagFQNs.forEach((tagFQN) => {
          const tagOption = tagOptionsMap.get(tagFQN);
          if (tagOption) {
            mlflowOperations.push(
              retryOperation(() =>
                updateModelTag({
                  name: modelName,
                  key: tagFQN,
                  value: tagOption.label,
                })
              )
            );
          }
        });

        removedTagFQNs.forEach((tagFQN) => {
          mlflowOperations.push(
            retryOperation(() =>
              deleteModelTag({
                name: modelName,
                key: tagFQN,
              })
            )
          );
        });

        if (mlflowOperations.length > 0) {
          try {
            await Promise.all(mlflowOperations);
          } catch (mlflowError) {
            console.error('MLflow tag sync failed after retry, rolling back OpenMetadata changes:', mlflowError);

            try {
              const rollbackOperations = generateTagPatchOperations(currentData?.tags || [], oldTagFQNs, availableTags);
              await updateAssetTags(entityType, entityId, rollbackOperations);
              console.warn('OpenMetadata rollback successful');
            } catch (rollbackError) {
              console.error('Rollback failed:', rollbackError);
            }

            throw new Error('MLflow 태그 동기화에 실패하여 변경사항이 롤백되었습니다.');
          }
        }
      }
    },
    onError: () => {
      notification.error({
        message: NOTIFICATION_MESSAGES.TAG_UPDATE_ERROR,
        description: NOTIFICATION_MESSAGES.TAG_UPDATE_ERROR_DESC,
        placement: 'bottomLeft',
        duration: 5,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: assetKeys.assetBaseInfo(variables.entityType, variables.fqn),
      });
      queryClient.invalidateQueries({
        queryKey: discoveryKeys.searchPrefix(),
      });

      notification.success({
        message: NOTIFICATION_MESSAGES.TAG_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.TAG_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });
};
